<template>
  <div class="drc">
    <!-- Header -->
    <div class="drc-head">
      <div class="drc-head-left">
        <div class="drc-head-ico" style="flex-shrink:0">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
        </div>
        <div>
          <div class="drc-title">Curva de retenção · {{ vslName }}</div>
          <div class="drc-sub">{{ activeView.subtitle }}</div>
        </div>
      </div>
      <div class="drc-tabs">
        <button v-for="t in tabs" :key="t" class="drc-tab" :class="{ on: activeTab === t }" @click="activeTab = t">{{ t }}</button>
      </div>
    </div>

    <!-- Meta strip -->
    <div class="drc-meta">
      <div class="drc-meta-item">
        <span class="drc-meta-lbl">Duração</span>
        <span class="drc-meta-val mono">{{ meta.duracao }}</span>
      </div>
      <div class="drc-meta-item">
        <span class="drc-meta-lbl">Retenção média</span>
        <span class="drc-meta-val mono" style="color:#28C76F">{{ meta.retencaoMedia }}</span>
      </div>
      <div class="drc-meta-item">
        <span class="drc-meta-lbl">Viewers únicos</span>
        <span class="drc-meta-val mono">{{ meta.viewersUnicos }}</span>
      </div>
      <div class="drc-meta-item">
        <span class="drc-meta-lbl">Completaram</span>
        <span class="drc-meta-val mono">{{ meta.completaram }} <span style="color:#F5B935;font-size:13px">· {{ meta.completaramPct }}</span></span>
      </div>
    </div>

    <!-- Active tab focus -->
    <div class="drc-focus">
      <div class="drc-focus-lbl">{{ activeView.focusLabel }}</div>
      <div class="drc-focus-val mono">{{ activeView.focusValue }}</div>
      <div class="drc-focus-hint">{{ activeView.focusHint }}</div>
    </div>

    <!-- Chart -->
    <div ref="chartWrap" class="drc-chart-wrap"
      @mousemove="onMouseMove"
      @mouseleave="onMouseLeave">

      <svg ref="svg" class="drc-svg" :viewBox="`0 0 ${VW} ${VH}`"
        preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="drc-clip">
            <rect :x="CL" :y="CT" :width="CW" :height="CH + 1" />
          </clipPath>
          <linearGradient id="drc-fill-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stop-color="#E86A10" stop-opacity="0.38"/>
            <stop offset="100%" stop-color="#E86A10" stop-opacity="0"/>
          </linearGradient>
        </defs>

        <!-- Chart background -->
        <rect :x="CL" :y="CT" :width="CW" :height="CH" fill="rgba(255,255,255,.015)" rx="2"/>

        <!-- Horizontal guide lines + Y labels -->
        <template v-for="g in yGuides">
          <line :key="'gl'+g.y" :x1="CL" :y1="g.y" :x2="CR" :y2="g.y"
            stroke="rgba(255,255,255,.06)" stroke-width="1"/>
          <text :key="'gt'+g.y" :x="CL - 6" :y="g.y + 4"
            fill="rgba(255,255,255,.28)" font-size="10"
            font-family="JetBrains Mono,monospace" text-anchor="end">{{ g.label }}</text>
        </template>

        <!-- Benchmark (nicho) — dashed blue -->
        <path v-if="activeView.showBenchmark" :d="benchmarkPath" fill="none" stroke="#318feb"
          stroke-width="1.5" stroke-dasharray="5 4" :opacity="activeView.benchmarkOpacity" clip-path="url(#drc-clip)"/>

        <!-- Replay zone rect -->
        <rect v-if="replayZonePt && activeView.showReplay"
          :x="replayZonePt.x1" :y="CT" :width="replayZonePt.width" :height="CH"
          fill="rgba(40,199,111,.06)" stroke="#28C76F"
          stroke-width="1" stroke-dasharray="3 3" opacity="0.7"/>

        <!-- Drop-off vertical line -->
        <line v-if="dropoffPt && activeView.showDropoff"
          :x1="dropoffPt.x" :y1="CT" :x2="dropoffPt.x" :y2="CB"
          stroke="#FF5A6B" stroke-width="1.2" stroke-dasharray="4 3" opacity="0.7"/>

        <!-- VSL curve fill -->
        <path v-if="fillPath" :d="fillPath" fill="url(#drc-fill-grad)" clip-path="url(#drc-clip)"/>
        <path v-else           :d="fallFill" fill="url(#drc-fill-grad)" clip-path="url(#drc-clip)"/>

        <!-- VSL curve line -->
        <path v-if="linePath" :d="linePath" fill="none" stroke="#E86A10"
          stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" clip-path="url(#drc-clip)"/>
        <path v-else          :d="fallLine" fill="none" stroke="#E86A10"
          stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" clip-path="url(#drc-clip)"/>

        <!-- Key point circles (clipped so they never go above chart) -->
        <circle v-if="dropoffPt && dropoffInBounds && activeView.showDropoff"
          :cx="dropoffPt.x" :cy="dropoffPt.y" r="5" fill="#FF5A6B" stroke="#07090D" stroke-width="2"/>
        <circle v-if="replayZonePt && replayInBounds && activeView.showReplay"
          :cx="replayZonePt.xMid" :cy="replayZonePt.y" r="5" fill="#28C76F" stroke="#07090D" stroke-width="2"/>

        <!-- Event labels at bottom axis (never overlap the curve) -->
        <g v-if="dropoffPt && activeView.showDropoff">
          <line :x1="dropoffPt.x" :y1="CB" :x2="dropoffPt.x" :y2="CB + 6"
            stroke="#FF5A6B" stroke-width="2"/>
          <rect :x="annDropX - annDropW/2" :y="CB + 7"
            :width="annDropW" height="18" rx="9"
            fill="rgba(255,90,107,.12)" stroke="rgba(255,90,107,.45)" stroke-width="1"/>
          <text :x="annDropX" :y="CB + 19.5"
            text-anchor="middle" font-size="9.5" font-weight="700"
            font-family="Inter,sans-serif" fill="#FF5A6B">
            Drop −{{ dropoffPt.dropPct }}% · {{ dropoffPt.time }}
          </text>
        </g>
        <g v-if="replayZonePt && activeView.showReplay">
          <line :x1="replayZonePt.xMid" :y1="CB" :x2="replayZonePt.xMid" :y2="CB + 6"
            stroke="#28C76F" stroke-width="2"/>
          <rect :x="annReplayX - annReplayW/2" :y="CB + 29"
            :width="annReplayW" height="18" rx="9"
            fill="rgba(40,199,111,.12)" stroke="rgba(40,199,111,.45)" stroke-width="1"/>
          <text :x="annReplayX" :y="CB + 41.5"
            text-anchor="middle" font-size="9.5" font-weight="700"
            font-family="Inter,sans-serif" fill="#28C76F">
            Replay zone · {{ replayZonePt.time }}
          </text>
        </g>

        <!-- X axis baseline -->
        <line :x1="CL" :y1="CB" :x2="CR" :y2="CB" stroke="rgba(255,255,255,.08)" stroke-width="1"/>

        <!-- X axis labels -->
        <text v-for="lbl in xAxisLabels" :key="lbl.label"
          :x="lbl.x" :y="CB + 74"
          fill="rgba(255,255,255,.28)" font-size="10"
          font-family="JetBrains Mono,monospace" text-anchor="middle">{{ lbl.label }}</text>

        <!-- ── HOVER LAYER (on top of everything) ── -->
        <!-- Invisible capture rect -->
        <rect :x="CL" :y="CT" :width="CW" :height="CH"
          fill="transparent" class="drc-capture"/>

        <!-- Crosshair vertical line -->
        <line v-if="hover" :x1="hover.svgX" :y1="CT" :x2="hover.svgX" :y2="CB"
          stroke="rgba(255,255,255,.22)" stroke-width="1" stroke-dasharray="3 2" pointer-events="none"/>

        <!-- Horizontal reference line at retention % -->
        <line v-if="hover" :x1="CL" :y1="hover.svgY" :x2="hover.svgX" :y2="hover.svgY"
          stroke="rgba(255,255,255,.10)" stroke-width="1" stroke-dasharray="2 2" pointer-events="none"/>

        <!-- Dot on curve at hover -->
        <circle v-if="hover" :cx="hover.svgX" :cy="hover.svgY" r="6"
          fill="#E86A10" stroke="white" stroke-width="2" pointer-events="none"/>
        <circle v-if="hover" :cx="hover.svgX" :cy="hover.svgY" r="3"
          fill="white" pointer-events="none"/>
      </svg>

      <!-- Floating tooltip -->
      <transition name="drc-tt-fade">
        <div v-if="hover" class="drc-tooltip" :style="tooltipStyle" pointer-events="none">
          <div class="drc-tt-time">{{ hover.time }}</div>
          <div class="drc-tt-divider"/>
          <div class="drc-tt-row">
            <span class="drc-tt-dot" style="background:#E86A10"/>
            <span class="drc-tt-lbl">Retenção</span>
            <span class="drc-tt-val" style="color:#E86A10">{{ hover.pct }}%</span>
          </div>
          <div class="drc-tt-row">
            <span class="drc-tt-dot" style="background:rgba(255,255,255,.5)"/>
            <span class="drc-tt-lbl">Viewers</span>
            <span class="drc-tt-val">{{ hover.viewers }}</span>
          </div>
          <div v-if="hover.isDropoff && activeView.showDropoff" class="drc-tt-row drc-tt-row--warn">
            <span class="drc-tt-dot" style="background:#FF5A6B"/>
            <span class="drc-tt-lbl">Drop-off crítico</span>
          </div>
          <div v-if="hover.isReplay && activeView.showReplay" class="drc-tt-row drc-tt-row--good">
            <span class="drc-tt-dot" style="background:#28C76F"/>
            <span class="drc-tt-lbl">Zona de replay</span>
          </div>
        </div>
      </transition>
    </div>

    <!-- Legend -->
    <div class="drc-legend">
      <div class="drc-leg-item"><span class="drc-leg-dot" style="background:#E86A10"/>&nbsp;Sua VSL</div>
      <div class="drc-leg-item"><span class="drc-leg-dot drc-leg-dot--dashed" style="background:#318feb"/>&nbsp;Benchmark do nicho</div>
      <div class="drc-leg-item"><span class="drc-leg-dot" style="background:#FF5A6B"/>&nbsp;Drop-off crítico</div>
      <div class="drc-leg-item"><span class="drc-leg-dot" style="background:#28C76F"/>&nbsp;Replay zone</div>
    </div>
  </div>
</template>

<script>
const VW = 800
const VH = 340    // taller to fit axis event labels below
const CL = 46
const CT = 16
const CR = 792
const CB = 242
const CW = CR - CL   // 746
const CH = CB - CT   // 226

export default {
  name: 'DashRetentionChart',
  props: {
    vslName:      { type: String, default: 'VSL Principal' },
    metaData:     { type: Object, default: null },
    retentionPts: { type: Array,  default: null },
  },
  data() {
    return {
      tabs: ['Retenção', 'Drop-off', 'Replays'],
      activeTab: 'Retenção',
      hover: null,
      VW, VH, CL, CT, CR, CB, CW, CH,
    }
  },
  computed: {
    meta() {
      return this.metaData || { duracao: '—', retencaoMedia: '—', viewersUnicos: '—', completaram: '—', completaramPct: '—' }
    },

    activeView() {
      const dropoffLabel = this.dropoffPt ? `−${this.dropoffPt.dropPct}% em ${this.dropoffPt.time}` : 'Sem queda crítica'
      const replayLabel = this.replayZonePt ? this.replayZonePt.time : 'Sem replay detectado'

      if (this.activeTab === 'Drop-off') {
        return {
          subtitle: 'Mostra a maior queda da curva e oculta o restante do foco analitico.',
          focusLabel: 'Queda maxima',
          focusValue: dropoffLabel,
          focusHint: this.dropoffPt ? `Ponto critico em ${this.dropoffPt.time}` : 'A curva nao mostra um drop-off forte no periodo.',
          showBenchmark: false,
          showDropoff: true,
          showReplay: false,
          benchmarkOpacity: 0,
        }
      }

      if (this.activeTab === 'Replays') {
        return {
          subtitle: 'Destaca a janela onde a audiencia volta a crescer.',
          focusLabel: 'Replay zone',
          focusValue: replayLabel,
          focusHint: this.replayZonePt ? `Retomada perto de ${this.replayZonePt.time}` : 'Nao encontrei uma zona clara de replay nesta VSL.',
          showBenchmark: true,
          showDropoff: false,
          showReplay: true,
          benchmarkOpacity: 0.3,
        }
      }

      return {
        subtitle: 'Percentual de espectadores assistindo segundo a segundo.',
        focusLabel: 'Visao geral',
        focusValue: this.meta.retencaoMedia,
        focusHint: 'Mantem a curva completa com os marcadores principais.',
        showBenchmark: true,
        showDropoff: true,
        showReplay: true,
        benchmarkOpacity: 0.55,
      }
    },

    yGuides() {
      return [
        { y: CT,              label: '100%' },
        { y: CT + CH * 0.25,  label: '75%'  },
        { y: CT + CH * 0.50,  label: '50%'  },
        { y: CT + CH * 0.75,  label: '25%'  },
      ]
    },

    _maxTimed() {
      if (!this.retentionPts?.length) return 0
      return this.retentionPts[this.retentionPts.length - 1].timed || 0
    },

    _maxTotal() {
      if (!this.retentionPts?.length) return 1
      return Math.max(1, ...this.retentionPts.map(p => p.total || 0))
    },

    _toSvg() {
      const maxTotal = this._maxTotal
      const maxTimed = this._maxTimed || 1
      return (t, n) => ({
        x: CL + (t / maxTimed) * CW,
        y: CB - Math.max(0, Math.min(1, n / maxTotal)) * CH,
      })
    },

    svgCurve() {
      const pts = this.retentionPts
      if (!pts || pts.length < 2) return null
      const toSvg = this._toSvg
      const step = Math.max(1, Math.floor(pts.length / 120))
      const sampled = pts.filter((_, i, arr) => i % step === 0 || i === arr.length - 1)
      return sampled.map(p => toSvg(p.timed, p.total))
    },

    linePath() {
      if (!this.svgCurve) return ''
      return this._smoothPath(this.svgCurve)
    },

    fillPath() {
      if (!this.svgCurve) return ''
      const line = this._smoothPath(this.svgCurve)
      const last = this.svgCurve[this.svgCurve.length - 1]
      const first = this.svgCurve[0]
      return `${line} L${last.x.toFixed(1)},${CB} L${first.x.toFixed(1)},${CB} Z`
    },

    benchmarkPath() {
      return `M ${CL},${CT + CH * 0.05} C ${CL + CW * 0.2},${CT + CH * 0.12} ${CL + CW * 0.5},${CT + CH * 0.38} ${CL + CW * 0.75},${CT + CH * 0.50} S ${CR - 20},${CT + CH * 0.52} ${CR},${CT + CH * 0.50}`
    },

    fallLine() {
      return `M ${CL},${CT + 2} C ${CL + 80},${CT + 4} ${CL + 180},${CT + CH * 0.10} ${CL + 240},${CT + CH * 0.66} C ${CL + 270},${CT + CH * 0.58} ${CL + 290},${CT + CH * 0.50} ${CL + 310},${CT + CH * 0.53} C ${CL + 340},${CT + CH * 0.56} ${CL + 370},${CT + CH * 0.60} ${CL + 420},${CT + CH * 0.64} C ${CL + 500},${CT + CH * 0.70} ${CL + 620},${CT + CH * 0.74} ${CR},${CT + CH * 0.78}`
    },

    fallFill() {
      return `${this.fallLine} L${CR},${CB} L${CL},${CB} Z`
    },

    xAxisLabels() {
      const maxTimed = this._maxTimed
      if (!maxTimed) {
        return [0, 2, 4, 6, 8, 10].map((m, i) => ({ x: CL + (i / 5) * CW, label: `${m}:00` }))
      }
      const count = 6
      return Array.from({ length: count + 1 }, (_, i) => ({
        x: Math.round(CL + (i / count) * CW),
        label: this.secToMin(Math.round((i / count) * maxTimed)),
      }))
    },

    dropoffPt() {
      const pts = this.retentionPts
      if (!pts || pts.length < 3) return null
      const maxTotal = this._maxTotal
      const maxTimed = this._maxTimed || 1
      let maxDrop = 0, dropIdx = 1
      for (let i = 1; i < pts.length; i++) {
        const drop = (pts[i - 1].total || 0) - (pts[i].total || 0)
        if (drop > maxDrop) { maxDrop = drop; dropIdx = i }
      }
      const dropPct = Math.min(100, Math.round((maxDrop / Math.max(1, pts[0].total || 0)) * 100))
      if (dropPct < 3) return null
      const pt = pts[dropIdx]
      const sv = this._toSvg(pt.timed, pt.total)
      return { x: sv.x.toFixed(1), y: sv.y.toFixed(1), time: this.secToMin(pt.timed), dropPct, timed: pt.timed }
    },

    replayZonePt() {
      const pts = this.retentionPts
      if (!pts || pts.length < 5) return null
      const maxTotal = this._maxTotal
      const maxTimed = this._maxTimed || 1
      let startIdx = null, endIdx = null
      for (let i = 1; i < pts.length - 1; i++) {
        if ((pts[i].total || 0) > (pts[i - 1].total || 0)) {
          if (startIdx === null) startIdx = i - 1
          endIdx = i
        } else if (startIdx !== null) break
      }
      if (startIdx === null || endIdx === null || endIdx <= startIdx) return null
      const x1   = CL + (pts[startIdx].timed / maxTimed) * CW
      const x2   = CL + (pts[endIdx].timed / maxTimed) * CW
      const midIdx = Math.round((startIdx + endIdx) / 2)
      const sv = this._toSvg(pts[midIdx].timed, pts[midIdx].total)
      return {
        x1: x1.toFixed(1),
        width: Math.max(x2 - x1, 20).toFixed(1),
        xMid: sv.x.toFixed(1),
        y: Math.max(CT + 8, sv.y - 8).toFixed(1),
        time: this.secToMin(pts[midIdx].timed),
        timed: pts[midIdx].timed,
      }
    },

    dropoffInBounds() {
      return this.dropoffPt && parseFloat(this.dropoffPt.y) >= CT && parseFloat(this.dropoffPt.y) <= CB
    },
    replayInBounds() {
      return this.replayZonePt && parseFloat(this.replayZonePt.y) >= CT && parseFloat(this.replayZonePt.y) <= CB
    },

    annDropW()   { return this.dropoffPt   ? Math.max(160, `Drop −${this.dropoffPt.dropPct}% · ${this.dropoffPt.time}`.length * 6.2 + 22)   : 160 },
    annReplayW() { return this.replayZonePt ? Math.max(140, `Replay zone · ${this.replayZonePt.time}`.length * 6.2 + 22)                    : 140 },

    annDropX() {
      if (!this.dropoffPt) return 0
      return Math.min(CR - this.annDropW / 2 - 4, Math.max(CL + this.annDropW / 2 + 4, parseFloat(this.dropoffPt.x)))
    },
    annReplayX() {
      if (!this.replayZonePt) return 0
      const rawX = parseFloat(this.replayZonePt.xMid)
      // If too close to dropoff label, shift right
      if (this.dropoffPt && Math.abs(rawX - parseFloat(this.dropoffPt.x)) < this.annReplayW * 0.6) {
        return Math.min(CR - this.annReplayW / 2 - 4, parseFloat(this.dropoffPt.x) + this.annDropW / 2 + this.annReplayW / 2 + 8)
      }
      return Math.min(CR - this.annReplayW / 2 - 4, Math.max(CL + this.annReplayW / 2 + 4, rawX))
    },

    tooltipStyle() {
      if (!this.hover) return {}
      const pctX = this.hover.svgX / VW
      const pctY = this.hover.svgY / VH
      const flipX = pctX > 0.70
      const flipY = pctY < 0.35
      return {
        left:      `${(pctX * 100).toFixed(1)}%`,
        top:       `${(pctY * 100).toFixed(1)}%`,
        transform: `translate(${flipX ? 'calc(-100% - 12px)' : '12px'}, ${flipY ? '4px' : 'calc(-100% - 4px)'})`,
      }
    },
  },
  methods: {
    secToMin(s) {
      const m   = Math.floor(s / 60)
      const sec = String(s % 60).padStart(2, '0')
      return `${m}:${sec}`
    },

    _smoothPath(points) {
      if (points.length < 2) return ''
      if (points.length === 2)
        return `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)} L${points[1].x.toFixed(1)},${points[1].y.toFixed(1)}`
      let d = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`
      for (let i = 1; i < points.length; i++) {
        const p0 = points[Math.max(0, i - 2)]
        const p1 = points[i - 1]
        const p2 = points[i]
        const p3 = points[Math.min(points.length - 1, i + 1)]
        const cp1x = p1.x + (p2.x - p0.x) / 6
        const cp1y = p1.y + (p2.y - p0.y) / 6
        const cp2x = p2.x - (p3.x - p1.x) / 6
        const cp2y = p2.y - (p3.y - p1.y) / 6
        d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`
      }
      return d
    },

    onMouseMove(e) {
      const svgEl = this.$refs.svg
      const pts   = this.retentionPts
      if (!svgEl || !pts || !pts.length) return

      const rect   = svgEl.getBoundingClientRect()
      const svgX   = ((e.clientX - rect.left) / rect.width) * VW
      if (svgX < CL || svgX > CR) { this.hover = null; return }

      // Convert svgX to time
      const maxTimed = this._maxTimed || 1
      const t = ((svgX - CL) / CW) * maxTimed

      // Binary search for closest point
      let lo = 0, hi = pts.length - 1
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (pts[mid].timed < t) lo = mid + 1
        else hi = mid
      }
      const near = (lo > 0 && Math.abs(pts[lo - 1].timed - t) < Math.abs(pts[lo].timed - t))
        ? pts[lo - 1] : pts[lo]

      const sv   = this._toSvg(near.timed, near.total)
      const pct  = Math.min(100, Math.round((near.total / this._maxTotal) * 100))
      const isDropoff = this.dropoffPt && Math.abs(near.timed - (this.dropoffPt.timed || 0)) < 30
      const isReplay  = this.replayZonePt && Math.abs(near.timed - (this.replayZonePt.timed || 0)) < 30

      this.hover = {
        svgX:   sv.x,
        svgY:   Math.max(CT + 1, Math.min(CB - 1, sv.y)),
        time:   this.secToMin(near.timed),
        pct,
        viewers: near.total,
        isDropoff,
        isReplay,
      }
    },

    onMouseLeave() {
      this.hover = null
    },
  },
}
</script>

<style scoped>
.drc {
  background: linear-gradient(180deg, rgba(22,29,43,.8), rgba(16,21,31,.9));
  border: 1px solid var(--wr-line-2);
  border-radius: 16px;
  padding: 22px;
  font-family: 'Inter', sans-serif;
  min-width: 0;
}
.drc-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; flex-wrap: wrap; }
.drc-head-left { display: flex; align-items: flex-start; gap: 12px; }
.drc-head-ico { width: 32px; height: 32px; border-radius: 9px; background: rgba(232,106,16,.12); color: #E86A10; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.drc-title { font-size: 14.5px; font-weight: 800; color: var(--wr-text); line-height: 1.3; }
.drc-sub { font-size: 12px; color: var(--wr-mute); margin-top: 2px; }
.drc-tabs { display: flex; gap: 3px; background: rgba(255,255,255,.04); border: 1px solid var(--wr-line-2); border-radius: 8px; padding: 3px; flex-shrink: 0; transform: scale(.92); transform-origin: right top; }
.drc-tab { padding: 5px 10px; border-radius: 5px; border: none; background: transparent; color: var(--wr-mute); font-size: 12.5px; font-weight: 500; font-family: 'Inter', sans-serif; cursor: pointer; transition: all .15s; white-space: nowrap; }
.drc-tab.on { background: rgba(232,106,16,.15); color: #F08023; font-weight: 600; }
.drc-meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px; }
.drc-meta-item { display: flex; flex-direction: column; gap: 3px; }
.drc-meta-lbl { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .6px; color: var(--wr-dim); }
.drc-meta-val { font-family: 'JetBrains Mono', monospace; font-size: 16px; font-weight: 700; color: var(--wr-text); font-variant-numeric: tabular-nums; }

.drc-focus {
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 10px 14px;
  align-items: center;
  margin-bottom: 18px;
  padding: 12px 14px;
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 12px;
  background: rgba(255,255,255,.03);
}
.drc-focus-lbl {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: var(--wr-dim);
}
.drc-focus-val {
  font-size: 17px;
  font-weight: 800;
  color: #E86A10;
  font-variant-numeric: tabular-nums;
}
.drc-focus-hint {
  font-size: 12px;
  color: var(--wr-mute);
  text-align: right;
}

/* Chart */
.drc-chart-wrap { position: relative; width: 100%; margin-bottom: 14px; user-select: none; }
.drc-svg { width: 100%; height: auto; display: block; overflow: visible; }
.drc-capture { cursor: crosshair; }

/* Tooltip */
.drc-tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(12, 15, 24, 0.95);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 10px;
  padding: 10px 13px;
  min-width: 148px;
  z-index: 20;
  box-shadow: 0 8px 24px rgba(0,0,0,.5);
  backdrop-filter: blur(6px);
}
.drc-tt-time {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255,255,255,.9);
  margin-bottom: 7px;
}
.drc-tt-divider { height: 1px; background: rgba(255,255,255,.08); margin-bottom: 7px; }
.drc-tt-row {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: rgba(255,255,255,.6);
  margin-bottom: 4px;
}
.drc-tt-row:last-child { margin-bottom: 0; }
.drc-tt-row--warn { color: #FF5A6B; }
.drc-tt-row--good { color: #28C76F; }
.drc-tt-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.drc-tt-lbl { flex: 1; }
.drc-tt-val { font-family: 'JetBrains Mono', monospace; font-weight: 700; font-size: 12.5px; color: rgba(255,255,255,.9); }

/* Tooltip transition */
.drc-tt-fade-enter-active { transition: opacity .1s ease, transform .1s ease; }
.drc-tt-fade-leave-active { transition: opacity .08s ease; }
.drc-tt-fade-enter { opacity: 0; transform: scale(.96); }
.drc-tt-fade-leave-to { opacity: 0; }

/* Legend */
.drc-legend { display: flex; gap: 18px; flex-wrap: wrap; padding-top: 12px; border-top: 1px solid var(--wr-line); }
.drc-leg-item { display: flex; align-items: center; gap: 7px; font-size: 12px; color: var(--wr-mute); }
.drc-leg-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.drc-leg-dot--dashed { opacity: .7; }
@media (max-width: 700px) {
  .drc-meta { grid-template-columns: repeat(2, 1fr); }
  .drc-focus { grid-template-columns: 1fr; }
  .drc-focus-hint { text-align: left; }
}
</style>
